import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import StaffLayout from "@/components/StaffLayout";
import MyCash from "@/pages/MyCash";
import { DEFAULT_PRODUCTS } from "@/lib/products";
import { useSearchParams } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { fmtINR, fmtNum, fmtDate, fmtTime } from "@/utils/format";
import { ChevronRight, Fuel, CalendarDays } from "lucide-react";

export default function MyShifts() {
  const nav = useNavigate();
  const [shifts, setShifts] = useState(null);
  const [params] = useSearchParams();
  const [tab, setTab] = useState(params.get("tab") || "month");

  useEffect(() => { api.get("/shifts/mine").then((r) => setShifts(r.data)); }, []);

  return (
    <StaffLayout>
      <div className="flex items-center justify-between mb-4 gap-3">
        <h1 className="text-2xl font-display font-bold">My Shifts</h1>
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList data-testid="myshifts-tabs">
            <TabsTrigger value="month" data-testid="tab-my-month">This Month</TabsTrigger>
            <TabsTrigger value="cash" data-testid="tab-my-cash">Cash A/c</TabsTrigger>
            <TabsTrigger value="history" data-testid="tab-history">History</TabsTrigger>
          </TabsList>
        </Tabs>
      </div>

      {tab === "month" ? <MyMonth /> : tab === "cash" ? <MyCash /> : (
        shifts === null ? (
          <div className="h-24 animate-pulse bg-slate-100 rounded-2xl" />
        ) : shifts.length === 0 ? (
          <Card className="p-8 rounded-2xl border-slate-200 text-center">
            <Fuel className="w-8 h-8 mx-auto text-slate-300" />
            <div className="mt-2 text-slate-600">No shifts recorded yet.</div>
          </Card>
        ) : (
          <div className="space-y-3">
            {shifts.map((s) => (
              <button
                key={s.id}
                data-testid={`my-shift-${s.id}`}
                onClick={() => nav(`/shift/${s.id}/summary`)}
                className="w-full text-left"
              >
                <Card className="p-4 rounded-2xl border-slate-200 hover:border-[color:var(--bpcl-blue)] transition-colors">
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="flex items-center gap-2 text-sm">
                        <Badge variant="secondary" className="rounded-full">Shift {s.shift_number}</Badge>
                        <span className="text-slate-500">{s.mpd}</span>
                        {s.status === "running" && (
                          <span className="inline-flex items-center gap-1 text-xs text-[color:var(--bpcl-orange-dark)] font-semibold">
                            <span className="w-1.5 h-1.5 bg-[color:var(--bpcl-orange)] rounded-full animate-pulse" /> Running
                          </span>
                        )}
                      </div>
                      <div className="font-display font-semibold text-base mt-1">{fmtDate(s.business_date)}</div>
                      <div className="text-xs text-slate-500 mt-0.5">
                        {fmtTime(s.start_time)} → {s.end_time ? fmtTime(s.end_time) : "—"}
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-[10px] uppercase text-slate-500 tracking-wide">Sales</div>
                      <div className="font-mono-num font-bold text-[color:var(--bpcl-blue)]">{fmtINR(s.totals?.grand_sales)}</div>
                      <div className="text-[11px] text-slate-500 font-mono-num mt-0.5">Net {fmtNum(s.totals?.net_total_litres)} L</div>
                    </div>
                    <ChevronRight className="w-4 h-4 text-slate-400 ml-2" />
                  </div>
                </Card>
              </button>
            ))}
          </div>
        )
      )}
    </StaffLayout>
  );
}

function MyMonth() {
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/shifts-monthly/mine").then((r) => setD(r.data)); }, []);
  const products = d?.products || DEFAULT_PRODUCTS;
  if (!d) return <div className="h-40 animate-pulse bg-slate-100 rounded-2xl" />;
  const t = d.totals;
  const monthLabel = new Date(d.year, d.month - 1).toLocaleString("en-IN", { month: "long", year: "numeric" });
  return (
    <div className="space-y-4" data-testid="my-month-section">
      <Card className="p-5 rounded-2xl border-transparent bg-gradient-to-br from-[color:var(--bpcl-blue)] to-[color:var(--bpcl-blue-dark)] text-white">
        <div className="flex items-center gap-2 text-xs uppercase tracking-wide text-white/80"><CalendarDays className="w-3.5 h-3.5" /> {monthLabel} · my total sales</div>
        <div className="mt-2 font-mono-num text-3xl font-bold" data-testid="my-month-total">{fmtINR(t.grand_sales)}</div>
        <div className={`mt-3 grid gap-2 text-center ${products.length > 3 ? "grid-cols-4" : "grid-cols-3"}`}>
          {products.map((p) => <Chip key={p.key} label={p.short} value={t[`net_${p.key}`]} unit={p.unit} />)}
        </div>
        <div className="mt-2 text-xs text-white/70">{t.shifts_count} shift(s) completed · {fmtNum(t.net_total_litres)} L total</div>
      </Card>

      <Card className="rounded-2xl border-slate-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="my-month-table">
            <thead className="bg-slate-50 text-[11px] uppercase text-slate-500 text-left">
              <tr>
                <th className="py-2.5 px-3">Date</th>
                {products.map((p) => <th key={p.key} className="py-2.5 px-3 text-right">{p.short} {p.unit}</th>)}
                <th className="py-2.5 px-3 text-right">Total ₹</th>
              </tr>
            </thead>
            <tbody className="font-mono-num">
              {d.days.length === 0 && <tr><td colSpan={2 + products.length} className="py-8 text-center text-slate-500 font-sans">No completed shifts this month yet.</td></tr>}
              {d.days.map((r) => (
                <tr key={r.business_date} data-testid={`my-day-${r.business_date}`} className="border-t border-slate-100">
                  <td className="py-2.5 px-3 font-sans font-medium whitespace-nowrap">{fmtDate(r.business_date)} <span className="text-slate-400 text-xs">· {r.shifts_count} shift</span></td>
                  {products.map((p) => <td key={p.key} className="py-2.5 px-3 text-right">{fmtNum(r[`net_${p.key}`])}</td>)}
                  <td className="py-2.5 px-3 text-right font-semibold text-[color:var(--bpcl-blue)]">{fmtINR(r.grand_sales)}</td>
                </tr>
              ))}
              {d.days.length > 0 && (
                <tr className="border-t-2 border-slate-300 bg-slate-50 font-bold">
                  <td className="py-2.5 px-3 font-sans">Month total</td>
                  {products.map((p) => <td key={p.key} className="py-2.5 px-3 text-right">{fmtNum(t[`net_${p.key}`])}</td>)}
                  <td className="py-2.5 px-3 text-right text-[color:var(--bpcl-blue)]">{fmtINR(t.grand_sales)}</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}

function Chip({ label, value, unit = "L" }) {
  return (
    <div className="rounded-xl bg-white/15 p-2">
      <div className="text-[10px] uppercase tracking-wide text-white/70">{label}</div>
      <div className="font-mono-num font-bold">{fmtNum(value)} {unit}</div>
    </div>
  );
}
